'use client';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AutocompleteField, SxProps, Theme } from 'ui';

import { Customer } from '~/data';

import { usePickerCustomers } from '../../../hooks/usePickerCustomers/usePickerCustomers';
import { useSelectedCustomerIds } from '../../../hooks/useSelectedCustomerIds/useSelectedCustomerIds';
import { CustomerItem } from './CustomerItem';
import { SearchField } from './SearchField';

interface Props {
  sx?: SxProps<Theme>;
}

const orderBySelected = (selectedCustomers: Customer[], customers: Customer[]): Customer[] => {
  return [
    ...selectedCustomers,
    ...customers.filter((customer) => !selectedCustomers.find((c) => c.customerId === customer.customerId)),
  ];
};

export const CustomerPicker = ({ sx }: Props) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<Customer[]>([]);
  const { selectedCustomerIds: selectedCustomersIds, setSelectedCustomerIds: setSelectedCustomersIds } =
    useSelectedCustomerIds();

  const { data: selectedCustomers } = usePickerCustomers({
    enabled: selectedCustomersIds.length > 0,
    filterBy: { customerIdIn: selectedCustomersIds.join(',') },
  });

  const handleChange = (customers: Customer[]) => {
    setOptions(customers);
  };

  const onCustomerClick = (customerId: string) => {
    if (selectedCustomersIds.includes(customerId)) {
      setSelectedCustomersIds(selectedCustomersIds.filter((id) => id !== customerId));
      return;
    }

    setSelectedCustomersIds([...selectedCustomersIds, customerId]);
  };

  const customerOptions = orderBySelected(selectedCustomers.items, options);

  return (
    <AutocompleteField<Customer>
      getOptionLabel={(option) => option.name}
      sx={{
        width: 210,
        ...sx,
      }}
      options={customerOptions}
      data-testid="customer-picker"
      noOptionsText={t('common:noResults')}
      filterOptions={() => customerOptions}
      renderOption={(_props, customer) => {
        return (
          <CustomerItem
            key={customer.customerId}
            customerId={customer.customerId}
            name={customer.name}
            checked={Boolean(selectedCustomersIds.find((selectedId) => selectedId === customer.customerId))}
            onClick={onCustomerClick}
          />
        );
      }}
      clearOnBlur={false}
      renderInput={(params) => (
        <SearchField params={params} onChange={handleChange} selectedCount={selectedCustomersIds.length} />
      )}
    />
  );
};
